import { useState, useEffect, useRef } from "react";
import { Copy, Check, Maximize2, Minimize2, Code, Image, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import Prism from 'prismjs';
import 'prismjs/components/prism-visual-basic';
import '../styles/prism-vba-theme.css';
interface Module {
  id: string;
  name: string;
  code: string;
}
interface ExcelFile {
  name: string;
  size: number;
  type: string;
  url?: string;
}
interface VBACodeBlockProps {
  modules: Module[];
  screenshotUrl?: string | null;
  excelFiles?: ExcelFile[];
  onCopy?: (moduleCode: string, moduleId: string) => void;
}
type ActiveTab = 'code' | 'preview' | 'workbook';
export const VBACodeBlock = ({
  modules,
  screenshotUrl,
  excelFiles = [],
  onCopy
}: VBACodeBlockProps) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [fontSize, setFontSize] = useState(15);
  const [activeTab, setActiveTab] = useState<ActiveTab>('code');
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const activeModule = modules[activeModuleIndex];
  const hasPreview = !!screenshotUrl;
  const hasWorkbook = excelFiles.length > 0;
  useEffect(() => {
    const checkOverflow = () => {
      if (codeContainerRef.current && activeTab === 'code') {
        const isOverflowing = codeContainerRef.current.scrollHeight > codeContainerRef.current.clientHeight;
        setHasOverflow(isOverflowing);
      }
    };
    checkOverflow();
    const timer = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timer);
  }, [activeModule, isExpanded, activeTab]);

  // Handle zoom with Ctrl/Cmd + scroll
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if ((e.ctrlKey || e.metaKey) && activeTab === 'code') {
        e.preventDefault();
        setFontSize(prev => {
          const delta = e.deltaY > 0 ? -1 : 1;
          return Math.max(12, Math.min(20, prev + delta));
        });
      }
    };
    const container = codeContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, {
        passive: false
      });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [activeTab]);
  const handleCopy = () => {
    if (activeModule) {
      navigator.clipboard.writeText(activeModule.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Code copied!");
      onCopy?.(activeModule.code, activeModule.id);
    }
  };
  return <div className="relative group w-full max-w-full min-w-0" data-vba-code-block onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {/* Code Container */}
      <div className={`
          relative rounded-xl overflow-hidden transition-all duration-300
          ${isHovered ? "shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]" : "shadow-[0_15px_50px_-12px_rgba(0,0,0,0.4)]"}
        `} style={{
      background: "linear-gradient(145deg, #1e293b 0%, #0f172a 50%, #1e3a5f 100%)",
      boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.8) inset, 0 1px 0 0 rgba(148, 163, 184, 0.1) inset"
    }}>
        {/* macOS Window Controls and Tabs */}
        <div className="absolute top-3 left-4 flex items-center gap-4 z-20">
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity" aria-label={isCollapsed ? "Expand code" : "Collapse code"}>
            <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            {isCollapsed && activeModule && <span className="ml-3 text-sm font-mono text-slate-400/70">
                {activeModule.name}
              </span>}
          </button>
          
          {/* Main Tabs: Code / Preview / Workbook */}
          {!isCollapsed && (hasPreview || hasWorkbook) && <div className="flex items-center gap-1 bg-slate-800/80 backdrop-blur-md rounded-lg p-0.5 border border-slate-600/30">
              <button onClick={() => setActiveTab('code')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'code' ? 'bg-slate-700/80 text-slate-100' : 'text-slate-300 hover:text-slate-100 hover:bg-slate-700/50'}`}>
                <Code className="h-3.5 w-3.5" />
                Code
              </button>
              {hasPreview && <button onClick={() => setActiveTab('preview')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'preview' ? 'bg-slate-700/80 text-slate-100' : 'text-slate-300 hover:text-slate-100 hover:bg-slate-700/50'}`}>
                  <Image className="h-3.5 w-3.5" />
                  Preview
                </button>}
              {hasWorkbook && <button onClick={() => setActiveTab('workbook')} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === 'workbook' ? 'bg-slate-700/80 text-slate-100' : 'text-slate-300 hover:text-slate-100 hover:bg-slate-700/50'}`}>
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                  Workbook
                </button>}
            </div>}
          
          {/* Module Tabs - Only show when Code tab is active */}
          {modules.length > 1 && !isCollapsed && activeTab === 'code' && <TooltipProvider delayDuration={300}>
              <div className="flex gap-1 ml-2 border-l border-white/10 pl-3">
                {modules.map((module, index) => <Tooltip key={module.id}>
                    <TooltipTrigger asChild>
                      <button onClick={e => {
                  e.stopPropagation();
                  setActiveModuleIndex(index);
                }} className={`px-3 py-1 text-xs font-mono rounded-md transition-colors ${index === activeModuleIndex ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}>
                        {module.name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="z-[9999]">
                      <p>Module {index + 1}: {module.name}</p>
                    </TooltipContent>
                  </Tooltip>)}
              </div>
            </TooltipProvider>}
        </div>

        {/* Copy Button - Only show for code tab */}
        {activeTab === 'code' && <div className="hidden md:block absolute top-3 right-4 z-10">
            <button onClick={handleCopy} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800/80 hover:bg-slate-700/80 backdrop-blur-md rounded-lg transition-all duration-200 text-slate-100 text-xs font-medium border border-slate-600/30 shadow-lg hover:shadow-xl hover:border-slate-500/50">
              {copied ? <>
                  <Check className="h-3.5 w-3.5" />
                  Copied
                </> : <>
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </>}
            </button>
          </div>}

        {/* Content Area */}
        {!isCollapsed && <>
            {/* Code Content */}
            {activeTab === 'code' && activeModule && <div ref={codeContainerRef} className={`relative pb-4 px-5 bg-sky-950 transition-all duration-300 min-h-[450px] w-full max-w-full overflow-x-auto overflow-y-auto ${modules.length > 1 || hasPreview || hasWorkbook ? 'pt-14' : 'pt-10'} ${isExpanded ? 'max-h-[800px]' : 'max-h-[450px]'}`}>
                <pre className="prism-vba-theme w-full font-mono leading-[1.6] text-slate-100 whitespace-pre" style={{
            fontSize: `${fontSize}px`
          }}>
                  <code className="language-visual-basic" dangerouslySetInnerHTML={{
              __html: Prism.highlight(activeModule.code, Prism.languages['visual-basic'], 'visual-basic')
            }} />
                </pre>
              </div>}

            {/* Preview Content */}
            {activeTab === 'preview' && screenshotUrl && <div className="bg-sky-950 p-8 pt-14 flex items-center justify-center min-h-[450px]">
                <img src={screenshotUrl} alt="Macro preview" className="max-w-full max-h-[400px] rounded-lg shadow-2xl border-0 border-transparent" />
              </div>}

            {/* Workbook Content */}
            {activeTab === 'workbook' && excelFiles.length > 0 && <div className="bg-sky-950 p-6 pt-14 min-h-[450px]">
                <div className="space-y-3">
                  {excelFiles.map((file, idx) => <div key={idx} className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <FileSpreadsheet className="h-6 w-6 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{file.name}</p>
                          <p className="text-sm text-slate-400">
                            {file.type?.toUpperCase() || "EXCEL"} • {((file.size || 0) / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      {file.url ? (
                        <a 
                          href={file.url} 
                          download={file.name}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white">
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </Button>
                        </a>
                      ) : (
                        <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white/50 cursor-not-allowed" disabled>
                          <Download className="h-4 w-4 mr-2" />
                          Unavailable
                        </Button>
                      )}
                    </div>)}
                </div>
              </div>}
          </>}
        
        {/* Overlay Controls (expand) - fixed at bottom-right, only for code tab */}
        {!isCollapsed && activeTab === 'code' && activeModule && isHovered && hasOverflow && <div className="pointer-events-none">
            <div className="absolute inset-x-0 bottom-3 flex justify-end z-20 pr-4">
              <div className="pointer-events-auto">
                <button onClick={() => setIsExpanded(!isExpanded)} className="p-2 hover:opacity-80 transition-all duration-200 text-slate-100" aria-label={isExpanded ? "Collapse code" : "Expand code"}>
                  {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>}

        {isCollapsed && <div className="pt-10 pb-4 px-5 bg-sky-950 transition-all duration-300 h-0 overflow-hidden" />}
      </div>
    </div>;
};